/**
 * The signed-in user's access and profile.
 *
 * ILLUSTRATIVE, and minimal on purpose. The only person here is the demo account
 * the rest of the fixtures already use; nothing invents a colleague, a phone
 * number, an address or a second identity to make a form look populated. Empty
 * fields render as empty, which is both honest and the whole point of a profile
 * page — there is something to fill in.
 *
 * The grant mirrors the platform's admin screen: a role held against a brand,
 * carrying combinations of category × rights holder/partner type × country,
 * where an absent value means every value.
 */

import type { AccessProfile, UserProfile } from '@/domain/access/model';

export const SESSION_ACCESS: AccessProfile = {
  grants: [
    {
      role: 'Application Admin',
      brand: null,
      combinations: [{ id: 1, category: null, partnerType: null, country: null }],
    },
  ],
  // Additive, and shown even though the grant above already covers everything:
  // being named on a partner says somebody put you there deliberately, which a
  // role that happens to include it does not.
  partners: [
    { partner: 'SK Rapid Wien', note: 'Named contact for the measurement programme' },
    { partner: "UEFA Women's Football", note: 'Pilot engagement' },
  ],
};

export const SESSION_PROFILE: UserProfile = {
  // A role mailbox, not a person's. The demo account is the one the rest of the
  // fixtures already use, and its address ends up in tracked source, in screen
  // recordings and in an exported contact list — so it is a shared address that
  // survives somebody leaving, rather than an individual's (DEC-46).
  workEmail: 'support@partnership-net.com',
  firstName: 'Paulo',
  lastName: 'Pinto',
  contactNumber: '',
  // Empty, like the phone and the address. A nationality for a real, named
  // person is a fact about that person, and this repo has no source for it —
  // the same rule that forbids inventing a coefficient forbids inventing this.
  nationality: '',
  language: '',
  currency: 'EUR',
  addressLine1: '',
  addressLine2: '',
  active: true,
};

/** Vocabularies the account form offers. Reference data, not personal data. */
export const PROFILE_VOCABULARY = {
  nationalities: [
    'Austria',
    'Belgium',
    'Denmark',
    'France',
    'Germany',
    'Ireland',
    'Italy',
    'Netherlands',
    'Portugal',
    'Spain',
    'Sweden',
    'Switzerland',
    'United Kingdom',
  ],
  languages: ['Deutsch', 'English', 'Español', 'Français', 'Italiano', 'Português'],
} as const;
